import React, { useState } from 'react';
import { Loader2, ShieldCheck, ShieldAlert, RefreshCcw, Zap, Search } from 'lucide-react';
import { Button } from './ui/button';

/**
 * StripeReadinessCard · Phase 2D
 *
 * Renders a live checklist of the Stripe configuration state, backed by
 * GET /api/admin/stripe-readiness-check. Purpose: let the admin answer
 * in 10 seconds "why is Stripe still in mock/test mode and what do I
 * need to change to go LIVE?".
 *
 * NEVER shows raw secret values — only kind (sk_live / sk_test /
 * missing) + presence booleans. Blockers are listed in plain language.
 */

const Row = ({ label, status, hint, testId, action }) => {
  const ok = status === 'ok';
  return (
    <div
      className={`flex items-center justify-between gap-2 rounded border px-3 py-2 ${
        ok ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-amber-500/40 bg-amber-500/5'
      }`}
      data-testid={testId}
    >
      <div className="min-w-0">
        <p className="text-[12px] font-semibold text-foreground">{label}</p>
        {hint && <p className="text-[10px] text-muted-foreground">{hint}</p>}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {action && !ok && action}
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 font-mono text-[10px] font-semibold ${
            ok ? 'bg-emerald-500/20 text-emerald-800' : 'bg-amber-500/20 text-amber-900'
          }`}
        >
          {ok ? '✅ ok' : '⚠️ ' + (status || 'missing')}
        </span>
      </div>
    </div>
  );
};

const StripeReadinessCard = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  // Phase 2D · Live-call test state — hits real Stripe API without charging.
  const [liveTesting, setLiveTesting] = useState(false);
  const [liveTest, setLiveTest] = useState(null);
  const [liveTestError, setLiveTestError] = useState('');
  // Phase 2D · Key inspector state — reveals kind + safe fingerprint.
  const [inspecting, setInspecting] = useState(false);
  const [inspection, setInspection] = useState(null);
  const [inspectError, setInspectError] = useState('');
  // Phase 2D · Quick-fix state — one-click activation of wallet_topup_v2.
  const [fixingTopupV2, setFixingTopupV2] = useState(false);

  const enableTopupV2 = async () => {
    setFixingTopupV2(true);
    try {
      const tok = localStorage.getItem('ig_token');
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
        body: JSON.stringify({ wallet_topup_v2: true }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j?.detail || `HTTP ${r.status}`);
      }
      // Re-run the readiness check so the UI reflects the new state.
      await run();
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setFixingTopupV2(false);
    }
  };

  const runInspect = async () => {
    setInspecting(true);
    setInspectError('');
    setInspection(null);
    try {
      const tok = localStorage.getItem('ig_token');
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/stripe-key-inspector`,
        { headers: { Authorization: `Bearer ${tok}` } },
      );
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail || `HTTP ${r.status}`);
      setInspection(j);
    } catch (e) {
      setInspectError(e?.message || String(e));
    } finally {
      setInspecting(false);
    }
  };

  const run = async () => {
    setLoading(true);
    setError('');
    setData(null);
    try {
      const tok = localStorage.getItem('ig_token');
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/stripe-readiness-check`,
        { headers: { Authorization: `Bearer ${tok}` } },
      );
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail || `HTTP ${r.status}`);
      setData(j);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  const runLiveTest = async () => {
    setLiveTesting(true);
    setLiveTestError('');
    setLiveTest(null);
    try {
      const tok = localStorage.getItem('ig_token');
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/stripe-live-call-test`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
        },
      );
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail || `HTTP ${r.status}`);
      setLiveTest(j);
    } catch (e) {
      setLiveTestError(e?.message || String(e));
    } finally {
      setLiveTesting(false);
    }
  };

  return (
    <div
      className="rounded-md border border-border bg-background/40 p-3"
      data-testid="stripe-readiness-card"
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <div>
          <p className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
            {data?.ready_for_live ? (
              <ShieldCheck className="h-4 w-4 text-emerald-600" />
            ) : (
              <ShieldAlert className="h-4 w-4 text-amber-600" />
            )}
            Stripe Live Readiness
          </p>
          <p className="text-[11px] text-muted-foreground">
            Verifica in un colpo d&apos;occhio se Stripe è pronto per andare LIVE su questa
            piattaforma. Non mostra mai le chiavi complete, solo il tipo e lo stato.
          </p>
        </div>
        <Button
          onClick={run}
          disabled={loading}
          size="sm"
          variant="outline"
          data-testid="stripe-readiness-run-btn"
        >
          {loading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <RefreshCcw className="mr-1 h-4 w-4" />}
          {loading ? 'Verifica…' : (data ? 'Ricontrolla' : 'Verifica')}
        </Button>
      </div>

      {error && (
        <p className="rounded bg-red-500/10 px-2 py-1 text-[11px] text-red-700" data-testid="stripe-readiness-error">
          ❌ {error}
        </p>
      )}

      {data && (
        <div className="mt-2 space-y-2" data-testid="stripe-readiness-result">
          <div
            className={`rounded border px-3 py-2 text-sm font-semibold ${
              data.ready_for_live
                ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-800'
                : 'border-amber-500/50 bg-amber-500/10 text-amber-900'
            }`}
            data-testid="stripe-readiness-overall"
          >
            {data.ready_for_live
              ? '✅ Pronto per LIVE — tutti i controlli passano.'
              : '⚠️ NON pronto per LIVE — vedi elementi mancanti sotto.'}
          </div>

          <div className="rounded border border-border bg-background/60 px-3 py-1.5 text-[11px]">
            <span className="text-muted-foreground">Top-up in uso adesso: </span>
            <span className="font-mono font-semibold text-foreground" data-testid="stripe-topup-flow">
              {data.topup_flow_used}
            </span>
          </div>

          <div className="grid grid-cols-1 gap-1.5 md:grid-cols-2">
            <Row
              testId="check-secret-key-present"
              label="Secret key presente"
              status={data.checks.secret_key_present ? 'ok' : 'missing'}
              hint="app_settings.stripe_secret_key"
            />
            <Row
              testId="check-secret-key-kind"
              label="Tipo secret key"
              status={data.checks.secret_key_kind === 'sk_live' ? 'ok' : data.checks.secret_key_kind}
              hint="Produzione richiede sk_live_…"
            />
            <Row
              testId="check-mock-mode"
              label="Mock mode"
              status={data.checks.mock_mode ? 'ON (must be OFF)' : 'ok'}
              hint="stripe_mock_mode"
            />
            <Row
              testId="check-topup-v2"
              label="wallet_topup_v2"
              status={data.checks.wallet_topup_v2 ? 'ok' : 'OFF (must be ON)'}
              hint="Attiva il vero Stripe Checkout per top-up"
              action={
                <Button
                  onClick={enableTopupV2}
                  disabled={fixingTopupV2}
                  size="sm"
                  className="h-6 gold-gradient px-2 text-[10px]"
                  data-testid="stripe-readiness-fix-topup-v2"
                >
                  {fixingTopupV2 ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
                  {fixingTopupV2 ? 'Attivo…' : 'Attiva ora'}
                </Button>
              }
            />
            <Row
              testId="check-payments-webhook"
              label="Payments webhook secret"
              status={data.checks.payments_webhook_configured ? 'ok' : 'missing'}
              hint="stripe_payments_webhook_secret · usato da /api/webhook/stripe-payments"
            />
            <Row
              testId="check-connect-webhook"
              label="Connect webhook secret"
              status={data.checks.connect_webhook_configured ? 'ok' : 'missing'}
              hint="stripe_connect_webhook_secret · usato da /api/webhook/stripe-connect"
            />
            <Row
              testId="check-connect-client-id"
              label="Connect client ID"
              status={data.checks.connect_client_id_configured ? 'ok' : 'missing'}
              hint="stripe_connect_client_id (ca_…)"
            />
            <Row
              testId="check-publishable-kind"
              label="Publishable key type"
              status={data.checks.publishable_key_kind === 'pk_live' ? 'ok' : data.checks.publishable_key_kind}
              hint="Non blocca il pagamento (checkout hosted) ma è buona igiene di config"
            />
          </div>

          {data.missing && data.missing.length > 0 && (
            <div
              className="rounded border border-amber-500/40 bg-amber-500/5 p-3"
              data-testid="stripe-readiness-missing"
            >
              <p className="mb-1.5 text-[12px] font-semibold text-amber-900">
                Elementi mancanti / da correggere ({data.missing.length}):
              </p>
              <ul className="list-disc space-y-0.5 pl-5 text-[11px] text-amber-900">
                {data.missing.map((m, i) => (
                  <li key={i} className="font-mono">{m}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Phase 2D · Key inspector — reveal kind + safe fingerprint. */}
          <div className="rounded border border-border bg-background/60 p-3" data-testid="stripe-key-inspector-block">
            <div className="mb-2 flex items-center justify-between gap-2">
              <div>
                <p className="flex items-center gap-1.5 text-[12px] font-semibold text-foreground">
                  <Search className="h-3.5 w-3.5 text-amber-600" />
                  Ispezione chiave attiva
                </p>
                <p className="text-[10px] text-muted-foreground">
                  Verifica ESATTAMENTE quale chiave sta usando il backend runtime (DB vs ENV), il tipo e un&apos;impronta sicura (primi 8 + ultimi 4).
                  La chiave completa non viene MAI mostrata.
                </p>
              </div>
              <Button
                onClick={runInspect}
                disabled={inspecting}
                size="sm"
                variant="outline"
                data-testid="stripe-key-inspector-btn"
              >
                {inspecting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Search className="mr-1 h-4 w-4" />}
                {inspecting ? 'Ispeziono…' : 'Ispeziona chiave'}
              </Button>
            </div>

            {inspectError && (
              <p className="rounded bg-red-500/10 px-2 py-1 text-[11px] text-red-700" data-testid="stripe-key-inspector-error">
                ❌ {inspectError}
              </p>
            )}

            {inspection && (
              <div className="space-y-2" data-testid="stripe-key-inspector-result">
                <div
                  className={`rounded border px-2 py-1.5 text-[12px] font-semibold ${
                    inspection.ok
                      ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-800'
                      : 'border-red-500/40 bg-red-500/10 text-red-800'
                  }`}
                  data-testid="stripe-key-inspector-overall"
                >
                  {inspection.ok
                    ? '✅ Chiave attiva OK · formato Standard Secret Key riconosciuto'
                    : `❌ Chiave attiva NON valida (kind=${inspection.active.kind})`}
                </div>

                <div className="grid grid-cols-1 gap-1 text-[11px] sm:grid-cols-2">
                  <div className="rounded bg-background/60 px-2 py-1">
                    <span className="text-muted-foreground">Tipo chiave: </span>
                    <span className={`font-mono font-semibold ${
                      inspection.active.kind === 'sk_live' ? 'text-emerald-700'
                      : inspection.active.kind === 'sk_test' ? 'text-amber-700'
                      : 'text-red-700'
                    }`} data-testid="inspect-kind">
                      {inspection.active.kind}
                    </span>
                  </div>
                  <div className="rounded bg-background/60 px-2 py-1">
                    <span className="text-muted-foreground">Sorgente: </span>
                    <span className="font-mono font-semibold" data-testid="inspect-source">
                      {inspection.active.source}
                    </span>
                  </div>
                  <div className="rounded bg-background/60 px-2 py-1">
                    <span className="text-muted-foreground">Lunghezza: </span>
                    <span className="font-mono font-semibold" data-testid="inspect-length">
                      {inspection.active.length} char
                    </span>
                  </div>
                  <div className="rounded bg-background/60 px-2 py-1">
                    <span className="text-muted-foreground">Livemode Stripe: </span>
                    <span className={`font-mono font-semibold ${
                      inspection.active.livemode_hint === true ? 'text-emerald-700'
                      : inspection.active.livemode_hint === false ? 'text-amber-700'
                      : 'text-muted-foreground'
                    }`} data-testid="inspect-livemode-hint">
                      {inspection.active.livemode_hint === null ? '—' : String(inspection.active.livemode_hint)}
                    </span>
                  </div>
                  <div className="rounded bg-background/60 px-2 py-1 sm:col-span-2">
                    <span className="text-muted-foreground">Impronta: </span>
                    <span className="font-mono font-semibold" data-testid="inspect-fingerprint">
                      {inspection.active.prefix || '(none)'}<span className="text-muted-foreground">…{inspection.active.length - 12 > 0 ? `${inspection.active.length - 12} char…` : ''}</span>{inspection.active.suffix}
                    </span>
                  </div>
                </div>

                <div className="rounded border border-border bg-background/40 px-2 py-1.5 text-[10px] text-muted-foreground" data-testid="inspect-runtime-info">
                  <span className="font-mono">runtime_reads_from:</span> {inspection.runtime_reads_from}
                </div>

                {/* Show side-by-side comparison DB vs ENV so any drift is obvious. */}
                <div className="grid grid-cols-1 gap-1.5 text-[10px] sm:grid-cols-2" data-testid="inspect-side-by-side">
                  <div className={`rounded border px-2 py-1.5 ${
                    inspection.db_key.present ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-border bg-background/40'
                  }`}>
                    <p className="font-mono font-semibold">DB (app_settings)</p>
                    {inspection.db_key.present ? (
                      <>
                        <p>kind: <span className="font-mono">{inspection.db_key.kind}</span></p>
                        <p>fp: <span className="font-mono">{inspection.db_key.prefix}…{inspection.db_key.suffix}</span></p>
                        <p>len: {inspection.db_key.length}</p>
                      </>
                    ) : (
                      <p className="italic text-muted-foreground">(assente)</p>
                    )}
                  </div>
                  <div className={`rounded border px-2 py-1.5 ${
                    inspection.env_key.present ? 'border-amber-500/30 bg-amber-500/5' : 'border-border bg-background/40'
                  }`}>
                    <p className="font-mono font-semibold">ENV (STRIPE_SECRET_KEY) · non usata a runtime</p>
                    {inspection.env_key.present ? (
                      <>
                        <p>kind: <span className="font-mono">{inspection.env_key.kind}</span></p>
                        <p>fp: <span className="font-mono">{inspection.env_key.prefix}…{inspection.env_key.suffix}</span></p>
                        <p>len: {inspection.env_key.length}</p>
                      </>
                    ) : (
                      <p className="italic text-muted-foreground">(assente)</p>
                    )}
                  </div>
                </div>

                {inspection.warnings && inspection.warnings.length > 0 && (
                  <div className="rounded border border-amber-500/40 bg-amber-500/5 p-2 text-[11px]" data-testid="inspect-warnings">
                    <p className="mb-1 font-semibold text-amber-900">Warning ({inspection.warnings.length}):</p>
                    <ul className="list-disc space-y-0.5 pl-4 text-amber-900">
                      {inspection.warnings.map((w, i) => (
                        <li key={i}>{w}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Phase 2D · Live-call test — hits real Stripe API without charges. */}
          <div className="rounded border border-border bg-background/60 p-3" data-testid="stripe-live-call-test-block">
            <div className="mb-2 flex items-center justify-between gap-2">
              <div>
                <p className="flex items-center gap-1.5 text-[12px] font-semibold text-foreground">
                  <Zap className="h-3.5 w-3.5 text-amber-600" />
                  Live-call test
                </p>
                <p className="text-[10px] text-muted-foreground">
                  Chiama Stripe reale con 3 step (Account.retrieve → Balance.retrieve → Session.create+expire).
                  <strong className="ml-1 text-emerald-700">Zero addebiti</strong> — la sessione viene scaduta immediatamente.
                </p>
              </div>
              <Button
                onClick={runLiveTest}
                disabled={liveTesting || !data.checks.secret_key_present}
                size="sm"
                variant="outline"
                data-testid="stripe-live-call-test-btn"
              >
                {liveTesting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Zap className="mr-1 h-4 w-4" />}
                {liveTesting ? 'Test…' : 'Esegui live-call test'}
              </Button>
            </div>

            {!data.checks.secret_key_present && (
              <p className="text-[11px] text-muted-foreground italic">
                Nessuna secret key salvata → il test è disabilitato. Salva prima una chiave Stripe.
              </p>
            )}

            {liveTestError && (
              <p className="rounded bg-red-500/10 px-2 py-1 text-[11px] text-red-700" data-testid="stripe-live-call-test-error">
                ❌ {liveTestError}
              </p>
            )}

            {liveTest && (
              <div className="space-y-2" data-testid="stripe-live-call-test-result">
                <div
                  className={`rounded border px-2 py-1.5 text-[12px] font-semibold ${
                    liveTest.overall_ok
                      ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-800'
                      : 'border-red-500/40 bg-red-500/10 text-red-800'
                  }`}
                  data-testid="stripe-live-call-overall"
                >
                  {liveTest.overall_ok ? '✅ PASS · Stripe risponde correttamente' : '❌ FAIL · Stripe ha rifiutato una o più chiamate'}
                </div>

                <div className="grid grid-cols-1 gap-1 text-[11px] sm:grid-cols-2">
                  <div className="rounded bg-background/60 px-2 py-1">
                    <span className="text-muted-foreground">Key kind: </span>
                    <span className="font-mono font-semibold">{liveTest.key_kind}</span>
                  </div>
                  <div className="rounded bg-background/60 px-2 py-1">
                    <span className="text-muted-foreground">Stripe livemode: </span>
                    <span className={`font-mono font-semibold ${liveTest.key_livemode ? 'text-emerald-700' : 'text-amber-800'}`}>
                      {liveTest.key_livemode === null ? '—' : String(liveTest.key_livemode)}
                    </span>
                  </div>
                  {liveTest.account?.id && (
                    <div className="rounded bg-background/60 px-2 py-1 sm:col-span-2">
                      <span className="text-muted-foreground">Account: </span>
                      <span className="font-mono">
                        {liveTest.account.id} · {liveTest.account.country} · {liveTest.account.default_currency?.toUpperCase()}
                        {typeof liveTest.account.charges_enabled === 'boolean' && (
                          <> · charges_enabled=<strong>{String(liveTest.account.charges_enabled)}</strong></>
                        )}
                        {typeof liveTest.account.payouts_enabled === 'boolean' && (
                          <> · payouts_enabled=<strong>{String(liveTest.account.payouts_enabled)}</strong></>
                        )}
                      </span>
                    </div>
                  )}
                </div>

                <ul className="space-y-1">
                  {liveTest.steps.map((s, i) => (
                    <li
                      key={i}
                      className={`rounded border px-2 py-1.5 text-[11px] ${
                        s.ok ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-red-500/40 bg-red-500/10'
                      }`}
                      data-testid={`stripe-live-call-step-${s.step}`}
                    >
                      <p className="font-mono font-semibold">
                        {s.ok ? '✅' : '❌'} {s.step}
                        {typeof s.elapsed_ms === 'number' && <span className="ml-2 font-normal text-muted-foreground">({s.elapsed_ms}ms)</span>}
                      </p>
                      {s.error && <p className="mt-0.5 font-mono text-red-800">{s.error}</p>}
                      {s.hint && <p className="mt-0.5 italic text-amber-800">💡 {s.hint}</p>}
                      {(s.session_id || s.status) && (
                        <p className="mt-0.5 text-muted-foreground">
                          {s.session_id && <>sessione: <code>{s.session_id}</code></>}
                          {s.status && <> · status: <code>{s.status}</code></>}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>

                {liveTest.overall_ok && liveTest.key_livemode === false && (
                  <div className="rounded border border-amber-500/40 bg-amber-500/10 p-2 text-[11px] text-amber-900">
                    ⚠️ Stripe risponde ma <strong>livemode=false</strong> — la chiave è in modalità TEST.
                    Per accettare pagamenti veri usa una chiave <code>sk_live_...</code>.
                  </div>
                )}
                {liveTest.overall_ok && liveTest.key_livemode === true && (
                  <div className="rounded border border-emerald-500/40 bg-emerald-500/10 p-2 text-[11px] text-emerald-900">
                    ✅ Stripe LIVE risponde correttamente. Puoi accettare pagamenti veri.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {!data && !loading && !error && (
        <p className="text-[11px] italic text-muted-foreground">
          Clicca &quot;Verifica&quot; per eseguire il check di readiness. Utile subito dopo aver
          salvato una chiave o dopo un redeploy in produzione.
        </p>
      )}
    </div>
  );
};

export default StripeReadinessCard;
